import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

interface WillCallToggleProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

/** Visual will-call switch — checkbox styled as a toggle. The label is the
 *  full clickable area for friendlier touch targets. */
export function WillCallToggle({
  checked,
  onCheckedChange,
  label = "Will Call",
  id,
  disabled,
  className,
}: WillCallToggleProps) {
  return (
    // min-h keeps the whole clickable label at a ≥44px touch target —
    // the visual track is only ~20px tall and this control changes
    // which fields are required.
    <label
      className={cn(
        "inline-flex min-h-[44px] select-none items-center gap-2 cursor-pointer",
        disabled && "cursor-not-allowed opacity-40",
        className,
      )}
    >
      {/* The sr-only checkbox is the focusable element; `peer` lets
          the visual track below render its keyboard focus ring. */}
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(c) => onCheckedChange(c === true)}
        disabled={disabled}
        className="peer sr-only"
      />
      <span
        className={cn(
          "relative inline-flex h-5 w-9 items-center rounded-full border border-border bg-foreground/[0.05] transition peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background",
          checked && "border-primary bg-primary/30",
        )}
      >
        <span
          className={cn(
            "absolute left-0.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full bg-foreground/40 transition",
            checked && "translate-x-4 bg-primary",
          )}
        />
      </span>
      <span className="text-xs font-semibold text-foreground/80">{label}</span>
    </label>
  );
}
